import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Environment, Project, Service } from "@/lib/types";
import { Panel } from "@/ui/Panel";
import { Head } from "@/ui/frame/Page";
import { Trail } from "@/ui/Trail";
import { EmptyState, ErrorState, Skeleton, TableSkeleton } from "@/ui/States";
import { Button } from "@/ui/Button";
import { Chip } from "@/ui/Chip";
import { StateLine } from "@/ui/Lamp";
import { Table, Td, Th, Tr } from "@/ui/Table";
import { CreateServiceDialog } from "@/features/services/CreateServiceDialog";
import { describeResources, ENVIRONMENT_LABELS } from "@/features/services/resources";
import { relativeTime, shortImage } from "@/lib/format";
import { cx } from "@/lib/format";
import { useToast } from "@/ui/Toast";
export const Route = createFileRoute("/_app/o/$orgId/projects/$projectId/")({
  component: ProjectScreen,
});

function ProjectScreen() {
  const { orgId, projectId } = Route.useParams();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [environmentKind, setEnvironmentKind] = useState<Environment["kind"]>("production");

  const project = useQuery({
    queryKey: ["project", orgId, projectId],
    queryFn: ({ signal }) =>
      api.get<{ project: Project }>(`/organisations/${orgId}/projects/${projectId}`, undefined, signal),
  });

  const services = useQuery({
    queryKey: ["services", orgId, projectId],
    queryFn: ({ signal }) =>
      api.get<{ services: Service[] }>(
        `/organisations/${orgId}/projects/${projectId}/services`,
        undefined,
        signal,
      ),
  });

  const remove = useMutation({
    mutationFn: () => api.delete<void>(`/organisations/${orgId}/projects/${projectId}`),
    onSuccess: async () => {
      toast.success("Project deleted");
      await queryClient.invalidateQueries({ queryKey: ["projects", orgId] });
    },
    onError: toast.error,
  });

  if (project.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-7 w-56" />
        <Skeleton className="h-40 w-full rounded-xl" />
      </div>
    );
  }
  if (project.isError) {
    return <ErrorState error={project.error} onRetry={() => void project.refetch()} />;
  }

  const p = project.data!.project;
  const environments = p.environments ?? [];
  const current = environments.find((e) => e.kind === environmentKind) ?? environments[0];
  const inEnvironment = (services.data?.services ?? []).filter(
    (s) => s.environment_id === current?.id,
  );

  return (
    <div className="flex flex-col gap-6">
      <Head
        above={
          <Trail
            trail={[
              { label: "Projects", to: "/o/$orgId/projects", params: { orgId } },
              { label: p.display_name },
            ]}
          />
        }
        title={p.display_name}
        description={p.description || "Services, databases and domains for this application."}
        actions={
          <>
            <Link to="/o/$orgId/projects/$projectId/databases" params={{ orgId, projectId }}>
              <Button tone="secondary">Databases</Button>
            </Link>
            <Button tone="primary" onClick={() => setCreating(true)} disabled={!current}>
              New service
            </Button>
          </>
        }
      />

      {current && (
        <CreateServiceDialog
          orgId={orgId}
          projectId={projectId}
          environments={environments}
          defaultEnvironmentId={current.id}
          open={creating}
          onOpenChange={setCreating}
        />
      )}

      {/* Environment tabs. Three fixed environments, so a tab strip rather than
          a dropdown: the choice is always visible and one click away. */}
      <div className="flex items-center gap-1 border-b border-[var(--border)]">
        {environments.map((environment) => {
          const count = (services.data?.services ?? []).filter(
            (s) => s.environment_id === environment.id,
          ).length;
          const active = environment.kind === environmentKind;
          return (
            <button
              key={environment.id}
              onClick={() => setEnvironmentKind(environment.kind)}
              aria-current={active ? "page" : undefined}
              className={cx(
                "relative -mb-px flex items-center gap-2 border-b-2 px-3 py-2 text-[13px] font-medium transition-colors",
                active
                  ? "border-[var(--primary)] text-[var(--text-primary)]"
                  : "border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
              )}
            >
              {ENVIRONMENT_LABELS[environment.kind] ?? environment.display_name}
              {count > 0 && <Chip>{count}</Chip>}
            </button>
          );
        })}
      </div>

      <Panel padded={false}>
        {services.isLoading && <TableSkeleton rows={3} columns={4} />}
        {services.isError && (
          <div className="px-5 py-4">
            <ErrorState error={services.error} onRetry={() => void services.refetch()} />
          </div>
        )}

        {services.data && inEnvironment.length === 0 && (
          <EmptyState
            title={`No services in ${ENVIRONMENT_LABELS[environmentKind]?.toLowerCase() ?? "this environment"}`}
            description="A service is one deployable part of your application: a web service that receives traffic, or a worker that does not."
            action={
              <Button tone="secondary" onClick={() => setCreating(true)} disabled={!current}>
                New service
              </Button>
            }
          />
        )}

        {inEnvironment.length > 0 && (
          <div className="px-5 py-4">
            <Table minWidth="42rem">
              <thead>
                <tr>
                  <Th>Service</Th>
                  <Th>State</Th>
                  <Th>Size</Th>
                  <Th>Source</Th>
                  <Th align="right">Updated</Th>
                </tr>
              </thead>
              <tbody>
                {inEnvironment.map((svc) => (
                  <Tr key={svc.id}>
                    <Td>
                      <Link
                        to="/o/$orgId/projects/$projectId/services/$serviceId"
                        params={{ orgId, projectId, serviceId: svc.id }}
                        className="flex flex-col"
                      >
                        <span className="font-medium text-[var(--text-primary)] hover:text-[var(--primary)]">
                          {svc.display_name}
                        </span>
                        <span className="mono text-[11px] text-[var(--text-muted)]">
                          {svc.slug} · {svc.kind === "web" ? "web service" : "worker"}
                        </span>
                      </Link>
                    </Td>
                    <Td>
                      <StateLine state={svc.state} />
                    </Td>
                    <Td className="text-[var(--text-secondary)]">{describeResources(svc.resources)}</Td>
                    <Td
                      className="mono max-w-[22rem] truncate text-[13px] text-[var(--text-secondary)]"
                      title={svc.git ? `${svc.git.repository}@${svc.git.branch}` : (svc.image ?? "")}
                    >
                      {svc.git ? `${svc.git.repository}@${svc.git.branch}` : shortImage(svc.image)}
                    </Td>
                    <Td align="right" className="text-[var(--text-secondary)]">
                      {relativeTime(svc.updated_at)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Panel>

      <Panel
        tone="down"
      title="Delete this project"
        description="Only possible once every service in it has been deleted."
      >
        <Button
          tone="danger"
          size="dense"
          busy={remove.isPending}
          disabled={(services.data?.services.length ?? 0) > 0}
          onClick={() => remove.mutate()}
        >
          Delete project
        </Button>
      </Panel>
    </div>
  );
}
